import type { ReactElement, ReactNode } from "react";
import { DrillKeyboardActivityContext } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill/keyboard-activity";
import {
  SuspensePageBoundary,
  StudyLayoutSkeleton,
} from "@-/dev.improve.haniverse.components.ui.suspense-page";
import type { MorphemeHrefObject } from "@-/dev.improve.haniverse.lib.morpheme.href.entities";
import {
  LearnFlashcardStage,
  type LearnFlashcardStageProps,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.deck._components.card";
import {
  DrillFlashcardControls,
  DrillFlashcardInfo,
  DrillFlashcardProvidersSuspense,
  DrillPageSuspense,
  GraphemeDrillFlashcardBack,
  GraphemeDrillFlashcardFront,
  WordDrillFlashcardBack,
  WordDrillFlashcardFront,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.drill";
import { createContext, memo, useCallback, useContext, useMemo } from "react";
import { learnCardPresentationKey } from "@-/dev.improve.haniverse.lib.learn.card-action";
import {
  RouteBodyRetentionProvider,
  RouteBodyStateProvider,
  RouteBodyStateScope,
} from "@-/dev.improve.haniverse.lib.navigation.route-body-state";
import { LearnReviewViewLifetime } from "#learn-review-view-lifetime";

type LearnReviewFlashcardProps = Omit<LearnFlashcardStageProps, "back" | "front" | "info"> & {
  canRetireViews: boolean;
  isCurrentPresentation: boolean;
  isDeckFocused: boolean;
  onSurfacesReleased: (presentationKey: string) => void;
  slot: LearnFlashcardStageProps["slot"] & { item: { kind: "review" } };
  suspenseFallback?: ReactElement;
};

const ReviewStageContext = createContext<{
  onRelease: () => void;
  props: LearnReviewFlashcardProps;
} | null>(null);

/**
 * A REVIEW item as a deck card: the drill front, the answer back (mnemonics, or the sentence and
 * per-character tiles for a word) and the Info surface, under one set of per-item providers.
 *
 * Related-reference links render inside this card (the deck's exploration), preserving the
 * revealed back and rating target; swiping while exploring returns to the review.
 */
export function LearnReviewFlashcard(props: LearnReviewFlashcardProps) {
  const { drillType, grapheme } = props.slot.item;
  const { onSurfacesReleased } = props;
  const presentationKey = learnCardPresentationKey(props.slot.presentation);
  const onRelease = useCallback(
    () => onSurfacesReleased(presentationKey),
    [onSurfacesReleased, presentationKey],
  );
  const morphemeHrefObject = useMemo<MorphemeHrefObject>(
    () => ({
      href: "/(nav)/(app)/learn/explore/morpheme/[morpheme]",
      params: { morpheme: grapheme },
    }),
    [grapheme],
  );
  return (
    <ReviewStageContext value={{ onRelease, props }}>
      <DrillKeyboardActivityContext value={props.isCurrentPresentation && props.isDeckFocused}>
        <LearnReviewFlashcardContent
          character={grapheme}
          contextSentence={props.slot.item.contextSentence ?? undefined}
          drillType={drillType}
          morphemeHrefObject={morphemeHrefObject}
          suspenseFallback={props.suspenseFallback}
        />
      </DrillKeyboardActivityContext>
    </ReviewStageContext>
  );
}

/** Stage and keyboard updates bypass the query chain; only actual card data enters it. */
// oxlint-disable-next-line no-top/no-top-level-side-effects -- React.memo creates a component descriptor without executing the component.
const LearnReviewFlashcardContent = /* @__PURE__ */ memo(function LearnReviewFlashcardContent({
  suspenseFallback,
  ...data
}: Omit<Parameters<typeof DrillPageSuspense>[0], "grapheme" | "word"> & {
  suspenseFallback?: ReactElement;
}) {
  return (
    <RouteBodyStateProvider>
      <RouteBodyRetentionProvider>
        <SuspensePageBoundary
          fallback={suspenseFallback ?? <StudyLayoutSkeleton />}
          location={import.meta.location}
        >
          <DrillPageSuspense
            {...data}
            grapheme={
              <DrillFlashcardProvidersSuspense>
                <LearnReviewFlashcardStage
                  backFace={<GraphemeDrillFlashcardBack />}
                  frontFace={<GraphemeDrillFlashcardFront />}
                />
              </DrillFlashcardProvidersSuspense>
            }
            word={
              <LearnReviewFlashcardStage
                backFace={<WordDrillFlashcardBack />}
                frontFace={<WordDrillFlashcardFront />}
              />
            }
          />
        </SuspensePageBoundary>
      </RouteBodyRetentionProvider>
    </RouteBodyStateProvider>
  );
});

/**
 * The deck stage for one review card. `frontFace` / `backFace` are the faces of the drill branch
 * the card belongs to; they are created by the memoized parent, so their identity is stable across
 * stage updates.
 */
function LearnReviewFlashcardStage({
  backFace,
  frontFace,
}: {
  backFace: ReactNode;
  frontFace: ReactNode;
}) {
  const state = useContext(ReviewStageContext);
  if (state === null) {
    throw new Error("Missing review stage owner");
  }
  const { onRelease, props } = state;
  // Their context consumers still update normally (answer edits, settings and promotion).
  const front = useMemo(
    () => <RouteBodyStateScope name="front">{frontFace}</RouteBodyStateScope>,
    [frontFace],
  );
  const back = useMemo(
    () => <RouteBodyStateScope name="back">{backFace}</RouteBodyStateScope>,
    [backFace],
  );
  const info = useMemo(
    () => (
      <RouteBodyStateScope name="info">
        <DrillFlashcardInfo />
      </RouteBodyStateScope>
    ),
    [],
  );
  const controls = useMemo(() => <DrillFlashcardControls />, []);
  return (
    <>
      {props.isCurrentPresentation ? controls : null}
      <LearnReviewViewLifetime
        canRetire={props.canRetireViews}
        isActive={props.isDeckFocused}
        onRelease={onRelease}
      >
        <LearnFlashcardStage {...props} back={back} front={front} info={info} />
      </LearnReviewViewLifetime>
    </>
  );
}
